import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from '../context/AuthContext';
import { tokens } from '../tokens';
import { useBreakpoint } from '../hooks/useBreakpoint';

/* ══════════════════════════════════════════════════════════════════
   FONT INJECTION
══════════════════════════════════════════════════════════════════ */
if (typeof document !== "undefined" && !document.getElementById("__prd_v3_fonts")) {
  const l = document.createElement("link");
  l.id = "__prd_v3_fonts"; l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&display=swap";
  document.head.appendChild(l);
}

/* ══════════════════════════════════════════════════════════════════
   DESIGN TOKENS
══════════════════════════════════════════════════════════════════ */
const C = {
  bgBase:      "#FBFBFA",
  bgCard:      "#FFFFFF",
  bgRaised:    "#F7F8F7",
  emeraldA:    "#00D97E",
  emeraldB:    "#00A651",
  emeraldFlat: "#16A34A",
  emeraldTint: "rgba(0,166,81,0.06)",
  emeraldGlow: "rgba(0,217,126,0.18)",
  emeraldBd:   "#A7F3D0",
  textH:       "#0A0F0D",
  textBody:    "#374151",
  textSub:     "#6B7280",
  textMeta:    "#9CA3AF",
  textFaint:   "#C4CBCF",
  gold:        "#B7881C",
  goldLight:   "#FEF3C7",
  goldBd:      "#FDE68A",
  amber:       "#D97706",
  danger:      "#DC2626",
  borderSolid: "rgba(0,0,0,0.06)",
  borderFaint: "rgba(0,0,0,0.04)",
  shadowSm:   "0 1px 4px rgba(0,0,0,0.04)",
  shadowCard: "0 4px 24px rgba(0,0,0,0.05)",
  shadowMd:   "0 8px 32px rgba(0,0,0,0.06)",
  shadowLg:   "0 24px 64px rgba(0,0,0,0.10)",
  shadowGlow: "0 0 0 3px rgba(0,166,81,0.12)",
  rXl:  "20px",
  rLg:  "16px",
  rMd:  "10px",
  rSm:  "8px",
  spring:  "cubic-bezier(0.34,1.56,0.64,1)",
  ease:    "cubic-bezier(0.22,1,0.36,1)",
  snappy:  "cubic-bezier(0.4,0,0.2,1)",
  font:    "'DM Sans', system-ui, sans-serif",
  serif:   "'Playfair Display', Georgia, serif",
};

const GRAD = `linear-gradient(135deg, ${C.emeraldA} 0%, ${C.emeraldB} 100%)`;

/* ══════════════════════════════════════════════════════════════════
   MOCK DATA
══════════════════════════════════════════════════════════════════ */
const MOCK = {
  firstName:"Amara", lastName:"Okafor",
  title:"Professional House Cleaner · 6 Years Experience",
  city:"Nairobi", category:"Cleaning & Laundry", categoryIcon:"🧺",
  experience:"6 Years", photo:null,
  serviceCount:3, proofCount:2,
  submittedAt:"21 Mar 2026, 10:42 AM",
  approvalEst:"28 Mar 2026",
  govId:true, selfie:true, credential1:true,
};

const SUBMITTED = new Date("2026-03-21T10:42:00");
const DEADLINE  = new Date(SUBMITTED.getTime() + 14*24*60*60*1000);
const NOW_DEMO  = new Date("2026-03-23T09:00:00");
const DAYS_LEFT = Math.max(0, Math.ceil((DEADLINE - NOW_DEMO)/(1000*60*60*24)));
const ELAPSED   = Math.min(100, Math.round(((NOW_DEMO-SUBMITTED)/(DEADLINE-SUBMITTED))*100));

/* ══════════════════════════════════════════════════════════════════
   GLOBAL CSS
══════════════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin:0; padding:0; }
  body { background:${C.bgBase}; font-family:${C.font}; font-size:13px; }
  ::-webkit-scrollbar { width:4px; }
  ::-webkit-scrollbar-thumb { background:#D1D5DB; border-radius:2px; }

  @keyframes fadeUp    { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  @keyframes fadeIn    { from{opacity:0} to{opacity:1} }
  @keyframes shimmerEmerald {
    0%   { background-position: -600px 0; }
    100% { background-position:  600px 0; }
  }
  @keyframes pulseDot {
    0%,100% { opacity:1; transform:scale(1); }
    50%      { opacity:0.4; transform:scale(1.6); }
  }
  @keyframes rippleOut { to{transform:scale(4);opacity:0} }
  .ripple-span {
    position:absolute; border-radius:50%;
    background:rgba(0,166,81,0.14);
    transform:scale(0); animation:rippleOut 0.55s linear;
    pointer-events:none;
  }
  @keyframes aiShimmer {
    0%   { background-position:200% center; }
    100% { background-position:-200% center; }
  }

  .prd-tooltip-wrap:hover .prd-tooltip { opacity:1; transform:translateY(0); pointer-events:auto; }
  .prd-tooltip {
    opacity:0; transform:translateY(5px); pointer-events:none;
    transition: all 0.2s ${C.ease};
    position:absolute; bottom:calc(100% + 9px); left:0;
    background:rgba(15,23,18,0.82);
    backdrop-filter:blur(12px); -webkit-backdrop-filter:blur(12px);
    color:#D1FAE5; font-size:11.5px; font-family:${C.font};
    line-height:1.55; padding:10px 14px; border-radius:${C.rMd};
    max-width:260px; white-space:normal; z-index:30;
    box-shadow:0 8px 24px rgba(0,0,0,0.24);
    border:1px solid rgba(167,243,208,0.15);
  }
  .prd-tooltip::after {
    content:""; position:absolute; top:100%; left:20px;
    border:5px solid transparent; border-top-color:rgba(15,23,18,0.82);
  }

  button:focus-visible { outline: 2px solid ${C.emeraldA}; outline-offset:2px; }

  .wyw-scroll { scrollbar-width: none; }
  .wyw-scroll::-webkit-scrollbar { display: none; }
`;

/* ══════════════════════════════════════════════════════════════════
   UTILITIES
══════════════════════════════════════════════════════════════════ */
function FadeIn({ children, delay=0, style={} }) {
  return (
    <div style={{ animation:`fadeUp 0.5s ${C.ease} ${delay}s both`, ...style }}>
      {children}
    </div>
  );
}

function Skel({ w="100%", h=14, r=8, mb=0, mt=0 }) {
  return (
    <div style={{
      width:w, height:h, borderRadius:r, marginBottom:mb, marginTop:mt,
      background:`linear-gradient(90deg, #EAF7EF 0%, #D1F0E2 40%, #EAF7EF 100%)`,
      backgroundSize:"600px 100%",
      animation:"shimmerEmerald 1.6s infinite linear",
      flexShrink:0,
    }}/>
  );
}

function useReady(ms=800) {
  const [ok,setOk]=useState(false);
  useEffect(()=>{ const t=setTimeout(()=>setOk(true),ms); return()=>clearTimeout(t); },[]);
  return ok;
}

function addRipple(e, el) {
  if (!el) return;
  const r=el.getBoundingClientRect();
  const sz=Math.max(r.width,r.height)*2;
  const x=e.clientX-r.left-sz/2, y=e.clientY-r.top-sz/2;
  const s=document.createElement("span");
  s.className="ripple-span";
  Object.assign(s.style,{width:sz+"px",height:sz+"px",left:x+"px",top:y+"px"});
  el.appendChild(s);
  s.addEventListener("animationend",()=>s.remove());
}

/* ══════════════════════════════════════════════════════════════════
   TOP NAV
══════════════════════════════════════════════════════════════════ */
function TopNav({ onExit }) {
  const [scrolled, setScrolled] = useState(false);
  const { isMobile, isTablet } = useBreakpoint();
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 4);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const navHeight = isMobile ? 56 : 72;
  const navPadding = isMobile ? "0 20px" : "0 48px";
  const logoFontSize = isMobile ? 18 : 22;

  return (
    <header style={{
      position: "sticky",
      top: 0,
      zIndex: 300,
      height: navHeight,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: navPadding,
      background: "#FFFFFF",
      borderBottom: "1px solid rgba(15,47,42,0.05)",
      boxShadow: scrolled ? "0px 4px 20px rgba(0,0,0,0.04)" : "none",
      transition: "box-shadow 0.25s ease",
    }}>
      <div style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontWeight: 700,
        fontSize: logoFontSize,
        color: "#0F2F2A",
        letterSpacing: "-0.5px",
        lineHeight: 1,
        cursor: "pointer",
        userSelect: "none",
        minHeight: isMobile ? tokens.spacing[44] : "auto",
        display: "inline-flex",
        alignItems: "center",
      }} onClick={onExit}>
        JiraniApp<span style={{ color: C.emeraldB }}>.</span>
      </div>

      <button
        onClick={onExit}
        onMouseEnter={e => e.currentTarget.style.background = "rgba(15,47,42,0.04)"}
        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        style={{
          background: "transparent",
          border: "1px solid rgba(15,47,42,0.10)",
          padding: isMobile ? "6px 12px" : "9px 16px",
          borderRadius: C.rMd,
          fontFamily: C.font,
          fontSize: isMobile ? 12 : 13,
          fontWeight: 500,
          color: "#0F2F2A",
          cursor: "pointer",
          letterSpacing: "-0.1px",
          transition: "background 0.18s ease",
          outline: "none",
          minHeight: isMobile ? tokens.spacing[44] : "auto",
        }}>
        Return to dashboard
      </button>
    </header>
  );
}

/* ══════════════════════════════════════════════════════════════════
   HERO VISUAL
══════════════════════════════════════════════════════════════════ */
function HeroVisual() {
  const { isMobile } = useBreakpoint();
  const heroHeight = isMobile ? 240 : 360;
  return (
    <div style={{
      position: "relative",
      borderRadius: 20,
      overflow: "hidden",
      height: heroHeight,
      flexShrink: 0,
      width: "100%",
      boxShadow: "0 4px 32px rgba(11,31,27,0.08)",
    }}>
      <img
        src="/images/hero-cleaning.png"
        alt="Professional at work"
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        onError={e => e.target.style.display = "none"}
      />
      <div style={{
        position: "absolute",
        inset: 0,
        background: "linear-gradient(to top, rgba(11,31,27,0.50) 0%, rgba(11,31,27,0.12) 40%, transparent 70%)",
        pointerEvents: "none",
      }}/>
      <div style={{
        position: "absolute",
        bottom: 20,
        left: 20,
        display: "flex",
        alignItems: "center",
        gap: 8,
      }}>
        <div style={{
          width: 6, height: 6, borderRadius: "50%",
          background: "#4ADE80", flexShrink: 0,
          animation: "pulseDot 3.5s ease-in-out infinite",
        }}/>
        <span style={{
          fontSize: isMobile ? 10 : 12, fontWeight: 500, fontFamily: C.font,
          color: "rgba(255,255,255,0.80)", letterSpacing: "0.01em",
        }}>
          Verified profile in progress
        </span>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   STATUS HERO
══════════════════════════════════════════════════════════════════ */
function StatusHero() {
  const { user } = useAuth();
  const { isMobile, isTablet } = useBreakpoint();
  const firstName = user?.name?.split(" ")[0] || "Amara";
  const lastName = user?.name?.split(" ")[1] || "Okafor";

  const heroPadding = isMobile ? "40px 0 32px" : "72px 0 48px";
  const gridColumns = isMobile ? "1fr" : "1fr 400px";
  const gridGap = isMobile ? 32 : 64;
  const titleFontSize = isMobile ? 28 : 38;
  const titleLineHeight = isMobile ? "36px" : "46px";
  const descFontSize = isMobile ? 14 : 15;

  return (
    <FadeIn delay={0.04}>
      <div style={{
        maxWidth: 1200,
        margin: "0 auto",
        padding: heroPadding,
        display: "grid",
        gridTemplateColumns: gridColumns,
        gap: gridGap,
        alignItems: "center",
      }}>
        <div>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            background: "rgba(111,143,134,0.10)",
            borderRadius: 999, padding: "5px 12px", marginBottom: 22,
          }}>
            <div style={{
              width: 6, height: 6, borderRadius: "50%",
              background: "#6F8F86", flexShrink: 0,
              animation: "pulseDot 3.5s ease-in-out infinite",
            }}/>
            <span style={{
              fontSize: isMobile ? 11.5 : 12.5, fontWeight: 500, fontFamily: C.font,
              color: "#6F8F86", letterSpacing: "0.01em",
            }}>
              Application under review
            </span>
          </div>

          <h1 style={{
            fontSize: titleFontSize, fontWeight: 500, color: "#0B1F1B",
            lineHeight: titleLineHeight, letterSpacing: "-0.5px",
            fontFamily: C.font, margin: "0 0 14px", maxWidth: 460,
          }}>
            Your application is<br/>under review.
          </h1>

          <p style={{
            fontSize: descFontSize, color: "#5C6F6A", lineHeight: 1.65,
            fontFamily: C.font, margin: "0 0 28px", maxWidth: 400,
            fontWeight: 400,
          }}>
            Our team is carefully verifying your credentials.<br/>
            No action is needed from you right now.
          </p>

          <div style={{
            width: 32, height: 1,
            background: "rgba(11,31,27,0.10)", marginBottom: 18,
          }}/>

          <p style={{
            fontSize: isMobile ? 11.5 : 12.5, color: "#9AA9A5", fontFamily: C.font,
            margin: 0, display: "flex", alignItems: "center", gap: 7,
            fontWeight: 400,
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="#9AA9A5" strokeWidth="2" strokeLinecap="round">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            Most applications are reviewed within 14 days
          </p>
        </div>

        <HeroVisual/>
      </div>
    </FadeIn>
  );
}

/* ══════════════════════════════════════════════════════════════════
   REVIEW TIMELINE
══════════════════════════════════════════════════════════════════ */
const STEPS = [
  { id: "submitted",      label: "Application Submitted",  sub: "Profile entered the queue",      state: "done",    date: "Mar 21, 2026" },
  { id: "id-verify",      label: "Identity Verification",  sub: "Government ID cross-checked",    state: "done",    date: "Mar 22, 2026" },
  { id: "profile-review", label: "Profile Quality Review", sub: "HR team is reviewing now",       state: "active",  date: "In progress"  },
  { id: "credentials",    label: "Credentials Check",      sub: "Certifications and references",  state: "pending", date: "Pending"      },
  { id: "final",          label: "Final Approval",         sub: "Access granted to the platform", state: "pending", date: "Est. Mar 28"  },
];

function ReviewTimeline() {
  const ready = useReady(700);
  const { isMobile } = useBreakpoint();
  const cardPadding = isMobile ? "24px 20px" : "32px 36px";
  const titleFontSize = isMobile ? 15 : 17;
  const stepLabelFontSize = isMobile ? 13 : 14;
  const stepSubFontSize = isMobile ? 11.5 : 12.5;
  const stepDateFontSize = isMobile ? 10.5 : 11.5;
  const stepGap = isMobile ? 20 : 24;

  return (
    <FadeIn delay={0.10} style={{height:"100%"}}>
      <div style={{
        background: "#FFFFFF",
        borderRadius: C.rXl,
        boxShadow: C.shadowCard,
        padding: cardPadding,
        height: "100%",
        boxSizing: "border-box",
      }}>
        <div style={{ marginBottom: 6 }}>
          <p style={{
            fontSize: titleFontSize, fontWeight: 500, fontFamily: C.font,
            color: "#0B1F1B", margin: "0 0 3px", letterSpacing: "-0.2px",
          }}>
            Review Timeline
          </p>
          <p style={{
            fontSize: isMobile ? 12 : 13, color: C.textMeta, fontFamily: C.font, margin: 0,
            fontWeight: 400,
          }}>
            Status updates at each stage of the review.
          </p>
        </div>

        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          margin: "20px 0 28px",
          padding: isMobile ? "8px 12px" : "12px 16px",
          borderRadius: C.rMd,
          background: "rgba(15,47,42,0.03)",
          border: "1px solid rgba(15,47,42,0.06)",
          flexWrap: "wrap",
          gap: 8,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{
              width: 7, height: 7, borderRadius: "50%",
              background: C.emeraldB,
              animation: "pulseDot 3s ease-in-out infinite",
              flexShrink: 0,
            }}/>
            <span style={{
              fontSize: isMobile ? 12 : 13, fontWeight: 500, fontFamily: C.font,
              color: "#0F2F2A",
            }}>In Progress</span>
          </div>
          <span style={{
            fontSize: isMobile ? 11.5 : 12.5, fontFamily: C.font,
            color: "#6b7c7c", fontWeight: 400,
          }}>
            Est. completion: {MOCK.approvalEst}
          </span>
        </div>

        {!ready ? (
          <div style={{ display: "flex", flexDirection: "column", gap: stepGap }}>
            {[1,2,3].map(i => (
              <div key={i} style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                <Skel w={16} h={16} r={8} />
                <div style={{ flex: 1 }}>
                  <Skel w="50%" h={13} r={4} mb={8} />
                  <Skel w="70%" h={11} r={4} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div style={{ position: "relative" }}>
              <div style={{
                position: "absolute", left: 7, top: 8, bottom: 8,
                width: 2,
                background: "linear-gradient(to bottom, rgba(15,47,42,0.18), rgba(15,47,42,0.04))",
              }} />
              <div style={{ display: "flex", flexDirection: "column", gap: stepGap }}>
                {STEPS.map((step) => {
                  const done   = step.state === "done";
                  const active = step.state === "active";
                  return (
                    <div key={step.id} style={{
                      display: "flex", alignItems: "flex-start",
                      gap: 20, position: "relative",
                      background: active ? "rgba(15,47,42,0.025)" : "transparent",
                      borderRadius: active ? C.rSm : 0,
                      padding: active ? (isMobile ? "6px 8px 6px 0" : "8px 10px 8px 0") : 0,
                      margin: active ? (isMobile ? "0 -8px" : "0 -10px") : 0,
                    }}>
                      <div style={{
                        width: 15, height: 15, borderRadius: "50%",
                        flexShrink: 0, marginTop: active ? 9 : 1,
                        marginLeft: active ? (isMobile ? 8 : 10) : 0,
                        zIndex: 1,
                        background: done ? "#0F2F2A" : active ? "#FFFFFF" : "#F0F2F1",
                        border: done ? "none" : active ? "2px solid #0F2F2A" : "1.5px solid #D8DCDB",
                        boxShadow: active ? "0 0 0 5px rgba(15,47,42,0.08)" : "none",
                        transition: "all 0.3s ease",
                      }} />
                      <div style={{ flex: 1, paddingBottom: 2 }}>
                        <p style={{
                          fontSize: stepLabelFontSize, fontWeight: active ? 600 : 500, fontFamily: C.font,
                          color: done || active ? "#0B1F1B" : "#C0C8C5",
                          margin: "0 0 2px", transition: "color 0.3s",
                        }}>
                          {step.label}
                        </p>
                        <p style={{
                          fontSize: stepSubFontSize, fontFamily: C.font,
                          color: active ? "#5C6F6A" : "#A0ADAA",
                          margin: "0 0 2px", lineHeight: 1.5,
                          fontWeight: 400, transition: "color 0.3s",
                        }}>
                          {step.sub}
                        </p>
                        <p style={{ fontSize: stepDateFontSize, fontFamily: C.font, color: "#B8C2BF", margin: 0, fontWeight: 400 }}>
                          {step.date}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={{
              marginTop: 24, paddingTop: 16,
              borderTop: "1px solid rgba(15,47,42,0.06)",
            }}>
              <p style={{
                fontSize: isMobile ? 11.5 : 12.5, color: "#6b7c7c", fontFamily: C.font,
                fontWeight: 400, margin: 0,
                display: "flex", alignItems: "center", gap: 6,
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                  stroke="#9AA9A5" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                We'll notify you once each step is complete.
              </p>
            </div>
          </>
        )}
      </div>
    </FadeIn>
  );
}

/* ══════════════════════════════════════════════════════════════════
   WHAT HAPPENS NEXT
══════════════════════════════════════════════════════════════════ */
const NEXT_STEPS = [
  { title: "Profile check",   body: "HR reviews your profile for completeness and professionalism." },
  { title: "Document review", body: "We verify your identity, credentials, and supporting documents." },
  { title: "Services review", body: "Your pricing and availability are evaluated against platform standards." },
  { title: "Clarifications",  body: "If we need more information, we'll reach out. Respond promptly." },
  { title: "Final decision",  body: "You'll receive a decision by email. Most reviews conclude in 14 days." },
];

function WhatHappensNext() {
  const { isMobile, isTablet } = useBreakpoint();
  const padding = isMobile ? "40px 0 40px" : "80px 0 80px";
  const gridColumns = isMobile ? "1fr" : (isTablet ? "repeat(3, 1fr)" : "repeat(5, 1fr)");
  const titleFontSize = isMobile ? 13 : 14;
  const bodyFontSize = isMobile ? 12 : 13;
  const stepNumberFontSize = isMobile ? 16 : 18;
  const gridGap = isMobile ? 32 : 0;

  return (
    <FadeIn delay={0.16}>
      <div style={{
        maxWidth: 1200,
        margin: "0 auto",
        padding,
        textAlign: "center",
      }}>
        <p style={{
          fontSize: isMobile ? 10 : 11, fontWeight: 600, letterSpacing: "0.10em",
          color: "#9AA9A5", fontFamily: C.font,
          textTransform: "uppercase", marginBottom: isMobile ? 32 : 40,
        }}>
          What happens next
        </p>

        <div style={{
          display: "grid", gridTemplateColumns: gridColumns, gap: gridGap, position: "relative",
        }}>
          {!isMobile && (
            <div style={{
              position: "absolute", top: 11, left: "10%", right: "10%",
              height: 1, background: "rgba(15,47,42,0.07)", zIndex: 0,
            }}/>
          )}

          {NEXT_STEPS.map((step, i) => (
            <div key={i} style={{
              display: "flex", flexDirection: "column",
              alignItems: "center", padding: isMobile ? "0 16px" : "0 16px",
              position: "relative", zIndex: 1,
            }}>
              <div style={{
                width: isMobile ? 28 : 22, height: isMobile ? 28 : 22, borderRadius: "50%",
                background: "#FFFFFF", display: "flex",
                alignItems: "center", justifyContent: "center",
                marginBottom: isMobile ? 16 : 20, flexShrink: 0,
              }}>
                <span style={{
                  fontFamily: C.serif, fontWeight: 700,
                  fontSize: stepNumberFontSize, color: "#CFE8E1",
                  lineHeight: 1, letterSpacing: "-0.02em",
                }}>
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <p style={{
                fontSize: titleFontSize, fontWeight: 500, fontFamily: C.font,
                color: "#0B1F1B", margin: "0 0 8px", lineHeight: 1.3,
              }}>
                {step.title}
              </p>
              <p style={{
                fontSize: bodyFontSize, fontWeight: 400, fontFamily: C.font,
                color: "#6F8F86", margin: 0, lineHeight: 1.6,
              }}>
                {step.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </FadeIn>
  );
}

/* ══════════════════════════════════════════════════════════════════
   WHILE YOU WAIT — with bi-directional arrow controls
══════════════════════════════════════════════════════════════════ */
function AISuggestBtn({ onClick }) {
  const [h,setH]=useState(false);
  const { isMobile } = useBreakpoint();
  return(
    <button onClick={onClick}
      onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)}
      style={{
        fontSize:isMobile?10:11,fontWeight:600,color:"#fff",fontFamily:C.font,
        background:`linear-gradient(270deg,${C.emeraldA},${C.emeraldB},${C.emeraldA})`,
        backgroundSize:"400% 100%",
        animation:"aiShimmer 4s linear infinite",
        border:"none",borderRadius:C.rMd,padding:isMobile?"4px 10px":"5px 12px",
        cursor:"pointer",display:"flex",alignItems:"center",gap:5,
        boxShadow:h?`0 4px 14px ${C.emeraldGlow}`:`0 2px 8px ${C.emeraldGlow}`,
        transform:h?"translateY(-1px)":"none",
        transition:`transform 0.18s ${C.ease}, box-shadow 0.18s ${C.ease}`,
        outline:"none",
        minHeight:isMobile?tokens.spacing[44]:"auto",
      }}>
      ✦ AI Suggest
    </button>
  );
}

const TIPS=[
  {icon:"📸",title:"Deep Clean",        body:"Pros with professional photos earn 3× more bookings in their first month.",  cta:"Update photo", aiSuggest:false, img:"/images/tip-deepclean.png"},
  {icon:"✍️",title:"Standard Clean",    body:"A compelling 3-sentence bio increases conversion by 40%. Be specific.",       cta:"Draft bio",    aiSuggest:true,  img:"/images/tip-standardclean.png"},
  {icon:"🏅",title:"Handyman Services", body:"Clients book pros with before/after photos 2× more often.",                  cta:"Add photos",   aiSuggest:false, img:"/images/tip-handyman.png"},
  {icon:"📍",title:"Laundry & Ironing", body:"Pros serving 3+ neighborhoods get 60% more matches.",                         cta:"Set areas",    aiSuggest:false, img:"/images/tip-laundry.png"},
];

function CarouselArrow({ direction, visible, onClick }) {
  const [h, setH] = useState(false);
  const { isMobile } = useBreakpoint();
  if (!visible) return null;

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        position: "absolute",
        top: "50%",
        [direction === "left" ? "left" : "right"]: isMobile ? -12 : -18,
        transform: "translateY(-50%)",
        zIndex: 10,
        width: isMobile ? 32 : 36,
        height: isMobile ? 32 : 36,
        borderRadius: "50%",
        background: h ? "rgba(15,47,42,0.05)" : "#FFFFFF",
        border: "1px solid rgba(15,47,42,0.08)",
        boxShadow: "0px 4px 10px rgba(0,0,0,0.07)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        outline: "none",
        transition: `all 0.18s ${C.ease}`,
        animation: "fadeIn 0.2s ease both",
        minHeight: isMobile ? tokens.spacing[44] : "auto",
      }}>
      <svg
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#0F2F2A"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ transform: direction === "left" ? "rotate(180deg)" : "none" }}>
        <path d="M5 12h14M12 5l7 7-7 7"/>
      </svg>
    </button>
  );
}

function WhileYouWait() {
  const [done,setDone]=useState([]);
  const [aiModal,setAiModal]=useState(false);
  const [aiLoading,setAiLoading]=useState(false);
  const [aiText,setAiText]=useState("");
  const scrollRef=useRef(null);
  const { isMobile, isTablet } = useBreakpoint();

  const [canScrollLeft,  setCanScrollLeft]  = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    return () => el.removeEventListener("scroll", updateScrollState);
  }, [updateScrollState]);

  function scrollRight() {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: isMobile ? 260 : 280, behavior: "smooth" });
  }
  function scrollLeft() {
    if (scrollRef.current) scrollRef.current.scrollBy({ left: - (isMobile ? 260 : 280), behavior: "smooth" });
  }

  function toggle(i){ setDone(p=>p.includes(i)?p.filter(x=>x!==i):[...p,i]); }

  function handleAI() {
    setAiModal(true); setAiLoading(true); setAiText("");
    setTimeout(()=>{
      setAiLoading(false);
      setAiText("I'm a meticulous home cleaning professional with 6 years of experience transforming Nairobi homes into pristine, welcoming spaces. Specializing in deep cleans, Airbnb turnovers, and move-in/out services, I bring all supplies and guarantee satisfaction. Your home deserves the Amara standard.");
    },1800);
  }

  const cardMinWidth = isMobile ? "calc(100% - 20px)" : (isTablet ? "calc(50% - 8px)" : "calc(33.333% - 10px)");
  const cardMaxWidth = isMobile ? "calc(100% - 20px)" : (isTablet ? "calc(50% - 8px)" : "calc(33.333% - 10px)");
  const cardPadding = isMobile ? "12px 14px 14px" : "14px 16px 16px";
  const titleFontSize = isMobile ? 12 : 13;
  const bodyFontSize = isMobile ? 11 : 12;
  const btnFontSize = isMobile ? 10 : 11;

  return(
    <>
      {aiModal&&(
        <div onClick={()=>setAiModal(false)} style={{position:"fixed",inset:0,
          background:"rgba(0,0,0,0.40)",backdropFilter:"blur(8px)",
          WebkitBackdropFilter:"blur(8px)",zIndex:500,
          display:"flex",alignItems:"center",justifyContent:"center",
          animation:"fadeIn 0.2s", padding: isMobile ? 16 : 0}}>
          <div onClick={e=>e.stopPropagation()} style={{background:C.bgCard,
            borderRadius:C.rXl,padding: isMobile ? "24px 20px" : "36px 40px",maxWidth:480,width:"100%",
            boxShadow:C.shadowLg,
            animation:`fadeUp 0.3s ${C.spring}`, margin: isMobile ? "0 16px" : 0}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20}}>
              <div style={{width:34,height:34,borderRadius:C.rSm,background:GRAD,
                display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>✦</div>
              <div>
                <p style={{fontSize: isMobile ? 13 : 14,fontWeight:500,color:C.textH,margin:0,fontFamily:C.font}}>AI Bio Suggestion</p>
                <p style={{fontSize: isMobile ? 10.5 : 11.5,color:C.textMeta,margin:0,fontFamily:C.font,fontWeight:400}}>Powered by JiraniApp AI</p>
              </div>
              <button onClick={()=>setAiModal(false)}
                style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",
                  fontSize:20,color:C.textMeta,lineHeight:1, minHeight: isMobile ? tokens.spacing[44] : "auto"}}>×</button>
            </div>
            {aiLoading?(
              <div style={{display:"flex",flexDirection:"column",gap:10,padding:"20px 0"}}>
                <Skel w="90%" h={13} r={5}/>
                <Skel w="75%" h={13} r={5}/>
                <Skel w="85%" h={13} r={5}/>
              </div>
            ):(
              <>
                <div style={{background:C.bgRaised,borderRadius:C.rMd,padding:"16px 18px",marginBottom:16}}>
                  <p style={{fontSize: isMobile ? 12.5 : 13.5,color:C.textBody,margin:0,fontFamily:C.font,lineHeight:1.7,fontWeight:400}}>
                    "{aiText}"
                  </p>
                </div>
                <div style={{display:"flex",gap:10}}>
                  <button onClick={()=>setAiModal(false)}
                    style={{flex:1,padding: isMobile ? "9px" : "11px",borderRadius:C.rMd,border:"none",
                      background:GRAD,color:"#fff",fontSize: isMobile ? 12 : 13,fontWeight:500,
                      fontFamily:C.font,cursor:"pointer", minHeight: isMobile ? tokens.spacing[44] : "auto"}}>
                    Use this bio
                  </button>
                  <button onClick={handleAI}
                    style={{padding: isMobile ? "9px 12px" : "11px 16px",borderRadius:C.rMd,
                      border:"none",background:C.bgRaised,
                      color:C.textBody,fontSize: isMobile ? 12 : 13,fontWeight:500,
                      fontFamily:C.font,cursor:"pointer", minHeight: isMobile ? tokens.spacing[44] : "auto"}}>
                    Regenerate
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <FadeIn delay={0.36}>
        <div style={{background:C.bgCard,borderRadius:C.rXl,
          boxShadow:C.shadowCard,padding: isMobile ? "20px 20px" : "28px 40px"}}>
          <div style={{marginBottom:20}}>
            <h2 style={{fontSize: isMobile ? 15 : 17,fontWeight:500,color:"#111827",margin:"0 0 4px",
              letterSpacing:"-0.01em",fontFamily:C.font}}>While You Wait</h2>
            <p style={{fontSize: isMobile ? 11.5 : 12.5,color:C.textMeta,margin:0,fontFamily:C.font,fontWeight:400}}>
              Browse services you can offer once approved.
            </p>
          </div>

          <div style={{ position: "relative", margin: "0 -4px" }}>

            {canScrollLeft && (
              <CarouselArrow direction="left" visible={canScrollLeft} onClick={scrollLeft} />
            )}
            {canScrollRight && (
              <CarouselArrow direction="right" visible={canScrollRight} onClick={scrollRight} />
            )}

            {canScrollLeft && (
              <div style={{
                position: "absolute", left: 0, top: 0, bottom: 0,
                width: isMobile ? 30 : 40, zIndex: 5, pointerEvents: "none",
                background: `linear-gradient(to right, ${C.bgCard}, transparent)`,
              }}/>
            )}
            {canScrollRight && (
              <div style={{
                position: "absolute", right: 0, top: 0, bottom: 0,
                width: isMobile ? 30 : 40, zIndex: 5, pointerEvents: "none",
                background: `linear-gradient(to left, ${C.bgCard}, transparent)`,
              }}/>
            )}

            <div
              ref={scrollRef}
              className="wyw-scroll"
              style={{
                display: "flex",
                gap: isMobile ? 12 : 14,
                overflowX: "auto",
                paddingBottom: 4,
                paddingLeft: 4,
                paddingRight: 4,
                scrollSnapType: "x mandatory",
              }}>
              {TIPS.map((tip,i)=>{
                const isDone=done.includes(i);
                return(
                  <div key={i}
                    style={{
                      minWidth: cardMinWidth, maxWidth: cardMaxWidth,
                      scrollSnapAlign:"start", flexShrink:0,
                      borderRadius:C.rLg, overflow:"hidden",
                      background:"#ffffff",
                      boxShadow:"0 8px 24px rgba(0,0,0,0.05), 0 2px 8px rgba(0,0,0,0.04)",
                      transition:"all 0.25s ease", cursor:"default",
                      display:"flex", flexDirection:"column",
                    }}
                    onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-4px)";e.currentTarget.style.boxShadow="0 16px 40px rgba(0,0,0,0.07), 0 4px 12px rgba(0,0,0,0.04)";}}
                    onMouseLeave={e=>{e.currentTarget.style.transform="none";e.currentTarget.style.boxShadow="0 8px 24px rgba(0,0,0,0.05), 0 2px 8px rgba(0,0,0,0.04)";}}>
                    <div style={{height: isMobile ? 90 : 110,background:"#E5E7EB",overflow:"hidden",position:"relative",flexShrink:0}}>
                      <img src={tip.img} alt={tip.title}
                        style={{width:"100%",height:"100%",objectFit:"cover",display:"block"}}
                        onError={e=>{e.currentTarget.style.display="none";}}/>
                      {isDone&&(
                        <div style={{position:"absolute",top:8,right:8,
                          background:GRAD,borderRadius:999,padding:"2px 10px",
                          fontSize: isMobile ? 9 : 10,fontWeight:600,color:"#fff",fontFamily:C.font}}>
                          Done ✓
                        </div>
                      )}
                    </div>
                    <div style={{
                      padding: cardPadding,
                      display:"flex", flexDirection:"column",
                      gap:"8px", flex:1,
                    }}>
                      <p style={{
                        fontSize: titleFontSize, fontWeight: 500, color:"#111827",
                        margin:0, fontFamily:C.font,
                        display:"block", marginBottom:2,
                      }}>
                        {tip.title}
                      </p>
                      <p style={{
                        fontSize: bodyFontSize, color:C.textMeta, margin:0,
                        fontFamily:C.font, lineHeight:1.55, fontWeight:400,
                        wordBreak:"break-word", flex:1,
                      }}>
                        {tip.body}
                      </p>
                      <div style={{display:"flex",gap:8,alignItems:"center",marginTop:"auto",paddingTop:4,flexWrap:"wrap"}}>
                        <button onClick={()=>toggle(i)}
                          style={{fontSize: btnFontSize,fontWeight:500,fontFamily:C.font,
                            color:isDone?C.textMeta:C.emeraldB,
                            background:isDone?"#F1F5F1":C.emeraldTint,
                            border:"none",
                            borderRadius:C.rMd,padding: isMobile ? "4px 10px" : "5px 12px",
                            cursor:"pointer",transition:`all 0.18s ${C.ease}`,outline:"none",
                            minHeight: isMobile ? tokens.spacing[44] : "auto"}}
                          onMouseEnter={e=>e.currentTarget.style.background=isDone?"#E5EAE5":"rgba(0,166,81,0.12)"}
                          onMouseLeave={e=>e.currentTarget.style.background=isDone?"#F1F5F1":C.emeraldTint}>
                          {isDone?"Undo":tip.cta}
                        </button>
                        {tip.aiSuggest&&!isDone&&(
                          <AISuggestBtn onClick={handleAI}/>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </FadeIn>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════
   REVIEW WINDOW
══════════════════════════════════════════════════════════════════ */
function ReviewWindow() {
  const ready = useReady(600);
  const { isMobile } = useBreakpoint();
  const FULL   = 2 * Math.PI * 60;
  const OFFSET = FULL - (FULL * ELAPSED / 100);
  const ringSize = isMobile ? 100 : 140;
  const ringRadius = isMobile ? 43 : 60;
  const strokeWidth = isMobile ? 7 : 9;
  const fullCirc = 2 * Math.PI * ringRadius;

  return (
    <FadeIn delay={0.14}>
      <div style={{
        background: "#FFFFFF",
        borderRadius: C.rXl,
        boxShadow: C.shadowCard,
        padding: isMobile ? 20 : 28,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        height: "100%",
      }}>
        {!ready ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
            <Skel w={ringSize} h={ringSize} r={ringSize/2} />
            <Skel w="70%" h={13} r={4} />
            <Skel w="90%" h={11} r={4} />
          </div>
        ) : (
          <>
            <div style={{ position: "relative", width: ringSize, height: ringSize, marginBottom: isMobile ? 18 : 22 }}>
              <svg width={ringSize} height={ringSize} viewBox={`0 0 ${ringSize} ${ringSize}`}
                style={{ transform: "rotate(-90deg)" }}>
                <defs>
                  <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%"   stopColor="#CFE8E1" />
                    <stop offset="100%" stopColor="#0F2F2A" />
                  </linearGradient>
                </defs>
                <circle cx={ringSize/2} cy={ringSize/2} r={ringRadius} fill="none"
                  stroke="rgba(15,47,42,0.07)" strokeWidth={strokeWidth} />
                <circle cx={ringSize/2} cy={ringSize/2} r={ringRadius} fill="none"
                  stroke="url(#ringGrad)" strokeWidth={strokeWidth} strokeLinecap="round"
                  strokeDasharray={fullCirc} strokeDashoffset={fullCirc - (fullCirc * ELAPSED / 100)}
                  style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(0.22,1,0.36,1)" }} />
              </svg>
              <div style={{
                position: "absolute", inset: 0,
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center", gap: 2,
              }}>
                <span style={{
                  fontSize: isMobile ? 13 : 15, fontWeight: 500, fontFamily: C.font,
                  color: "#0B1F1B", letterSpacing: "-0.2px", lineHeight: 1,
                }}>
                  Amara O.
                </span>
                <span style={{
                  fontSize: isMobile ? 10 : 11.5, fontFamily: C.font, color: "#9AA9A5",
                  marginTop: 4, lineHeight: 1.4, fontWeight: 400,
                }}>
                  Est. approval date
                </span>
                <span style={{
                  fontSize: isMobile ? 11.5 : 12.5, fontWeight: 500, fontFamily: C.font,
                  color: "#0F2F2A", lineHeight: 1,
                }}>
                  {MOCK.approvalEst}
                </span>
              </div>
            </div>

            <p style={{
              fontSize: isMobile ? 13 : 14, fontWeight: 500, fontFamily: C.font,
              color: "#0B1F1B", margin: "0 0 6px", letterSpacing: "-0.1px",
            }}>
              Your review is in progress
            </p>
            <p style={{
              fontSize: isMobile ? 12 : 13, fontWeight: 400, fontFamily: C.font,
              color: "#6F8F86", margin: 0, lineHeight: 1.6, maxWidth: isMobile ? 180 : 200,
            }}>
              {DAYS_LEFT} days remaining. Your profile is under review. Sit tight, we’ll notify you once it’s complete.
            </p>
          </>
        )}
      </div>
    </FadeIn>
  );
}

/* ══════════════════════════════════════════════════════════════════
   STATUS SIDEBAR — Review Window + Submitted Documents only
══════════════════════════════════════════════════════════════════ */
function StatusSidebar() {
  const { isMobile } = useBreakpoint();
  const gridColumns = isMobile ? "1fr" : "1fr 1fr";

  return (
    <div style={{display:"grid",gridTemplateColumns:gridColumns,gap: isMobile ? 16 : 20,alignItems:"stretch"}}>
      <ReviewWindow />

      <FadeIn delay={0.18}>
        <div style={{background:"#ffffff",borderRadius:C.rXl,
          boxShadow:C.shadowCard,
          padding: isMobile ? "18px" : "22px",height:"100%",display:"flex",flexDirection:"column"}}>
          <p style={{fontSize: isMobile ? 15 : 16,fontWeight:500,color:"#111827",margin:"0 0 14px",
            letterSpacing:"-0.01em",fontFamily:C.font}}>
            Submitted documents
          </p>
          {[
            {label:"Government ID", sub:"KRA PIN matched",   done:MOCK.govId,       type:"pdf"},
            {label:"Selfie photo",  sub:"Identity confirmed", done:MOCK.selfie,      type:"jpg"},
            {label:"Credential",   sub:"Credential.pdf",     done:MOCK.credential1, type:"pdf"},
          ].map((item,i)=>(
            <div key={i}
              style={{display:"flex",alignItems:"center",gap:10,
                marginBottom:i<2?10:0,cursor:"default",
                background:C.bgRaised,
                borderRadius:C.rMd, padding: isMobile ? "8px 10px" : "10px 12px",
                transition:"background 0.18s ease"}}
              onMouseEnter={e=>e.currentTarget.style.background="#EDEEED"}
              onMouseLeave={e=>e.currentTarget.style.background=C.bgRaised}>
              <div style={{
                width: isMobile ? 32 : 36,height: isMobile ? 32 : 36,borderRadius:8,flexShrink:0,
                background:item.done?"#ecfdf5":"#f3f4f6",
                display:"flex",alignItems:"center",justifyContent:"center",
                flexDirection:"column",gap:1}}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                  stroke={C.emeraldB} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                <span style={{fontSize:7,fontWeight:700,fontFamily:"monospace",
                  color:C.emeraldB,letterSpacing:"0.02em",lineHeight:1}}>
                  {item.type?.toUpperCase()}
                </span>
              </div>
              <div style={{flex:1,minWidth:0}}>
                <p style={{fontSize: isMobile ? 12 : 13,fontWeight:500,color:"#111827",margin:"0 0 1px",
                  fontFamily:C.font,lineHeight:1.2}}>{item.label}</p>
                <p style={{fontSize: isMobile ? 10 : 11,color:C.textMeta,margin:0,
                  fontFamily:C.font,lineHeight:1.4,fontWeight:400}}>{item.sub}</p>
              </div>
              <div style={{width:18,height:18,borderRadius:"50%",
                background:C.emeraldTint,
                display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <svg width="9" height="9" viewBox="0 0 12 12" fill="none"
                  stroke={C.emeraldB} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="2 6 5 9 10 3"/>
                </svg>
              </div>
            </div>
          ))}
        </div>
      </FadeIn>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   TRUST BLOCK (final section)
══════════════════════════════════════════════════════════════════ */
function TrustBlock() {
  const [hovered, setHovered] = useState(false);
  const { isMobile } = useBreakpoint();
  const navigate = useNavigate();

  const handleContactSupport = () => {
    navigate("/support");
  };

  return (
    <FadeIn delay={0.10}>
      <div style={{
        background: "#FFFFFF",
        borderRadius: C.rXl,
        boxShadow: C.shadowCard,
        padding: isMobile ? "24px 20px" : "32px 36px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 20,
        flexDirection: isMobile ? "column" : "row",
        textAlign: isMobile ? "center" : "left",
      }}>
        <div style={{ flex: 1 }}>
          <p style={{
            fontSize: isMobile ? 16 : 17, fontWeight: 500, fontFamily: C.font,
            color: "#0B1F1B", margin: "0 0 8px", letterSpacing: "-0.2px",
          }}>
            Your information is handled with care.
          </p>
          <p style={{
            fontSize: isMobile ? 12 : 13, fontWeight: 400, fontFamily: C.font,
            color: "#5C6F6A", margin: 0, lineHeight: 1.65,
          }}>
            All documents are securely stored and reviewed only for verification.
          </p>
        </div>
        <button
          onClick={handleContactSupport}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          style={{
            background: hovered ? "#1a3d37" : "#0F2F2A",
            color: "#FFFFFF", border: "none",
            padding: isMobile ? "9px 18px" : "11px 20px", borderRadius: C.rMd,
            fontSize: isMobile ? 12 : 13, fontWeight: 500, fontFamily: C.font,
            cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
            letterSpacing: "-0.1px",
            transition: `background 0.18s ${C.ease}`,
            outline: "none",
            minHeight: isMobile ? tokens.spacing[44] : "auto",
          }}>
          Contact support
        </button>
      </div>
    </FadeIn>
  );
}

/* ══════════════════════════════════════════════════════════════════
   ROOT
══════════════════════════════════════════════════════════════════ */
export default function ProReviewDashboard() {
  const navigate = useNavigate();
  const { isLoggedIn, isPro } = useAuth();
  const { isMobile, isTablet } = useBreakpoint();

  useEffect(() => {
    if (!isLoggedIn || !isPro) {
      navigate('/');
    }
  }, [isLoggedIn, isPro, navigate]);

  const containerPadding = isMobile ? "0 20px" : "0 40px";
  const mainPadding = isMobile ? "0 0 60px" : "0 0 100px";
  const gridColumns = isMobile ? "1fr" : "7fr 5fr";
  const gridGap = isMobile ? 24 : 32;
  const whatHappensMargin = isMobile ? 48 : 72;
  const trustBlockMargin = isMobile ? 48 : 80;

  return(
    <div style={{minHeight:"100vh",fontFamily:C.font,background:C.bgBase}}>
      <style>{GLOBAL_CSS}</style>
      <TopNav onExit={()=>navigate("/")}/>

      <div style={{maxWidth:1300,margin:"0 auto",padding:containerPadding}}>
        <div style={{maxWidth:1200,margin:"0 auto",padding:mainPadding}}>

          <StatusHero/>

          <div style={{
            display: "grid",
            gridTemplateColumns: gridColumns,
            gap: gridGap,
            alignItems: "stretch",
          }}>
            <div style={{display:"flex",flexDirection:"column"}}>
              <ReviewTimeline/>
            </div>

            <div style={{
              display: "flex",
              flexDirection: "column",
              gap: isMobile ? 16 : 20,
              overflow: "hidden",
            }}>
              <StatusSidebar/>
              <div style={{flex: 1, minHeight: 0, overflow: "hidden"}}>
                <WhileYouWait/>
              </div>
            </div>
          </div>

          <div style={{marginTop: whatHappensMargin}}>
            <WhatHappensNext/>
          </div>

          <div style={{marginTop: trustBlockMargin}}>
            <TrustBlock/>
          </div>

        </div>
      </div>
    </div>
  );
}